import { MaggieHttp } from '../MaggieHttp';
import {
  ICompanyListsResponse,
  ICompanyNewsResponse,
  ICompanyProfileResponse,
  ICompanySummaryResponse,
  companyListsResponseSchema,
  companyNewsResponseSchema,
  companyProfileResponseSchema,
  companySummaryResponseSchema,
} from '../../schemas/CompanyProfile.schema';

/**
 * Lookup payload for the unified company-profile endpoint. Exactly one of the
 * fields should be provided; the backend enforces this via a zod `.refine`.
 * The order of precedence (CB UUID > HM URN > HM id > PB id > domain > LinkedIn)
 * mirrors the server-side logic in `resolvePrimaryKey`.
 */
export interface ICompanyProfileLookup {
  readonly domain?: string;
  readonly linkedinUrl?: string;
  readonly cbUuid?: string;
  readonly hmEntityUrn?: string;
  readonly hmId?: number;
  readonly pbCompanyId?: string;
}

/**
 * Fetch the unified company profile (merged Crunchbase / Harmonic / PitchBook
 * data) for a single entity. Sync, single request.
 *
 * Throws if the server returns a non-2xx response or the payload fails the
 * top-level shape validation. Deeply nested fields are validated leniently —
 * see `CompanyProfile.schema.ts` for the rationale.
 */
export async function fetchCompanyProfile(
  lookup: ICompanyProfileLookup,
  signal?: AbortSignal
): Promise<ICompanyProfileResponse> {
  const { data } = await MaggieHttp.sourcing.get<unknown>('/api/v1/company-profile', {
    params: lookup,
    signal,
  });
  return companyProfileResponseSchema.validateSync(data, { stripUnknown: false });
}

/**
 * Fetch recent news for a single company. Same lookup contract as the
 * profile endpoint (one of `domain` / `cbUuid` / `hmEntityUrn` / `hmId` /
 * `pbCompanyId` / `linkedinUrl`). The response is a list of
 * `{ source, date, title, url }` items already deduped + sorted
 * newest-first by the backend.
 *
 * Errors throw — callers (typically a React Query hook) decide how to
 * surface failures vs. empty results.
 */
export async function fetchSourcingCompanyNews(
  lookup: ICompanyProfileLookup,
  signal?: AbortSignal
): Promise<ICompanyNewsResponse> {
  const { data } = await MaggieHttp.sourcing.get<unknown>('/api/v1/company-profile/news', {
    params: lookup,
    signal,
  });
  return companyNewsResponseSchema.validateSync(data, { stripUnknown: false });
}

/**
 * Fetch the Foresight AI narrative summary for a single company. Returns
 * a list of short bullet strings (3–5 typically). The backend forwards to
 * the agents service and may legitimately return an empty list when no
 * summary can be generated yet — the UI panel hides itself in that case.
 */
export async function fetchSourcingCompanySummary(
  lookup: ICompanyProfileLookup,
  signal?: AbortSignal
): Promise<ICompanySummaryResponse> {
  const { data } = await MaggieHttp.sourcing.get<unknown>('/api/v1/company-profile/summary', {
    params: lookup,
    signal,
  });
  return companySummaryResponseSchema.validateSync(data, { stripUnknown: false });
}

/**
 * Fetch the saved-lists membership for a single company. Same lookup
 * contract as the profile endpoint. Returns the set of `SavedList` rows
 * (entityType=company, not deleted) that already contain the company —
 * the UI uses this to:
 *  - drive the live `Added to N lists` count on the profile header, and
 *  - filter the "Add to list" picker so the user can't add a duplicate.
 *
 * Errors throw — callers (typically a React Query hook) decide how to
 * surface failures vs. empty results. A Snowflake failure during
 * indirect resolution is gracefully downgraded server-side to a 200
 * with `lists: []`, so a thrown error here is genuinely unexpected.
 */
export async function fetchCompanyLists(
  lookup: ICompanyProfileLookup,
  signal?: AbortSignal
): Promise<ICompanyListsResponse> {
  const { data } = await MaggieHttp.sourcing.get<unknown>('/api/v1/company-profile/lists', {
    params: lookup,
    signal,
  });
  return companyListsResponseSchema.validateSync(data, { stripUnknown: false });
}

export interface ISourcingTypeaheadResult {
  id: string;
  provider: string;
  sourceId: string;
  domain: string | null;
  name: string;
  website?: string | null;
  logo?: string | null;
  type?: string | null;
}

const MIN_SEARCH_LENGTH = 2;
const DEFAULT_LIMIT = 10;

let searchSourcingController: AbortController | undefined;

export async function searchSourcingCompanies(
  searchTerm: string,
  limit: number = DEFAULT_LIMIT,
  abortConsecutive = false
): Promise<ISourcingTypeaheadResult[]> {
  if (!searchTerm || searchTerm.length < MIN_SEARCH_LENGTH) {
    return [];
  }

  const client = MaggieHttp.sourcing;
  if (!client) {
    return [];
  }

  if (searchSourcingController && abortConsecutive) {
    searchSourcingController.abort();
  }
  searchSourcingController = new AbortController();

  try {
    const { data: response } = await client.get<{ data: ISourcingTypeaheadResult[] }>(
      '/api/v1/companies/typeahead',
      {
        params: { q: searchTerm, pageSize: limit },
        signal: searchSourcingController.signal,
      }
    );

    searchSourcingController = undefined;
    return Array.isArray(response?.data) ? response.data : [];
  } catch (e) {
    if ((e as Error)?.name !== 'CanceledError') {
      console.error('Sourcing typeahead search failed:', e);
    }
    return [];
  }
}
